import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { empresaPerfilSchema, REGIMES_TRIBUTARIOS, ROTULO_REGIME, type EmpresaPerfilDto, type EmpresaPerfilResponse } from '@triade/contracts';
import { useEmpresaPerfil, useSalvarEmpresaPerfil } from '@/api/empresa';
import { useMe } from '@/api/me';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { MensagemCampo } from '@/components/item-cadastro/dados-item';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { buscarCep } from '@/lib/cep';
import { ApiError } from '@/lib/api';
import { useSessao } from '@/lib/session';
import { LogoEmpresa } from './logo-empresa';
import { MenusEmpresa } from './menus-empresa';
import { ParametrosEmpresa } from './parametros-empresa';
import { deResposta, FORM_VAZIO, podeEditarPerfil } from './tipos';

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

type Form = UseFormReturn<EmpresaPerfilDto, unknown, EmpresaPerfilDto>;

/** Campo de texto ligado ao formulario; a validacao e o schema dos contratos (o mesmo da API). */
function Campo({ form, nome, rotulo, desabilitado, maxLength, className, placeholder, inputMode, aoSair, rodape }: {
  form: Form; nome: Exclude<keyof EmpresaPerfilDto, 'regimeTributario' | 'rodapePdf' | 'sobre'>; rotulo: string; desabilitado: boolean; maxLength?: number; className?: string; placeholder?: string; inputMode?: 'numeric' | 'email' | 'tel' | 'url' | 'text';
  /** Chamado ao sair do campo (depois do onBlur do formulario). */
  aoSair?: () => void;
  /** Texto extra abaixo do campo (ex.: aviso discreto de busca de CEP). */
  rodape?: React.ReactNode;
}) {
  const erro = form.formState.errors[nome]?.message;
  const registro = form.register(nome);
  return (
    <div className={className}>
      <Label htmlFor={`p-${nome}`} className={ROTULO}>
        {rotulo}
      </Label>
      <Input
        id={`p-${nome}`}
        maxLength={maxLength}
        disabled={desabilitado}
        placeholder={placeholder}
        inputMode={inputMode}
        aria-invalid={erro ? true : undefined}
        aria-describedby={erro ? `p-${nome}-erro` : undefined}
        {...registro}
        onBlur={(e) => {
          void registro.onBlur(e);
          aoSair?.();
        }}
      />
      <MensagemCampo id={`p-${nome}-erro`} mensagem={erro} />
      {rodape}
    </div>
  );
}

export type AbaEmpresa = 'dados' | 'parametros' | 'menus';
/** Na URL, `?secao=parametros` ou `?secao=menus` abre a aba; sem ela, Dados. */
export interface BuscaEmpresa {
  secao?: 'parametros' | 'menus';
}
export function lerBuscaEmpresa(bruto: Record<string, unknown>): BuscaEmpresa {
  return bruto.secao === 'parametros' || bruto.secao === 'menus' ? { secao: bruto.secao } : {};
}

/** Tela "Empresa" (/configuracoes/empresa): abas Dados (cabecalho do PDF, fiscal, logo) e Parametros (com vigencia). */
export function EmpresaPerfilPage({ busca = {} }: { busca?: BuscaEmpresa }) {
  const { empresaId } = useSessao();
  const me = useMe();
  const navigate = useNavigate();
  const verParametros = me.data?.permissoes.includes('parametros.ver') ?? false;
  const verMenus = me.data?.permissoes.includes('empresas.gerenciar') ?? false;
  const aba: AbaEmpresa = busca.secao === 'parametros' && verParametros ? 'parametros' : busca.secao === 'menus' && verMenus ? 'menus' : 'dados';

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 pb-4">
      <div>
        <div className="text-small text-ink-muted">
          Configurações <span aria-hidden="true">/</span>{' '}
          <Link to="/configuracoes/empresa" className="text-ink-muted hover:underline">
            Empresa
          </Link>
        </div>
        <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Empresa</h1>
        <p className="mt-1 text-ink-secondary">Dados cadastrais, logo e parâmetros comerciais com vigência.</p>
      </div>
      {empresaId === null ? (
        <Vazio>Escolha a empresa no menu lateral para ver os dados da empresa.</Vazio>
      ) : (
        <Tabs value={aba} onValueChange={(v) => void navigate({ to: '/configuracoes/empresa', search: v === 'parametros' || v === 'menus' ? { secao: v } : {}, replace: true })}>
          <TabsList aria-label="Seções da empresa">
            <TabsTrigger value="dados">Dados</TabsTrigger>
            {verParametros && <TabsTrigger value="parametros">Parâmetros</TabsTrigger>}
            {verMenus && <TabsTrigger value="menus">Menus</TabsTrigger>}
          </TabsList>
          <TabsContent value="dados" className="flex flex-col gap-5">
            <DadosEmpresa key={empresaId} />
          </TabsContent>
          {verParametros && (
            <TabsContent value="parametros">
              <ParametrosEmpresa key={empresaId} editavel={me.data?.permissoes.includes('parametros.editar') ?? false} />
            </TabsContent>
          )}
          {verMenus && (
            <TabsContent value="menus">
              <MenusEmpresa key={empresaId} editavel={verMenus} />
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
}

function DadosEmpresa() {
  const perfil = useEmpresaPerfil();
  if (perfil.isError) return <ErroFormacao erro={perfil.error} onTentar={() => void perfil.refetch()} />;
  if (!perfil.data) return <Vazio papel="status">Carregando dados da empresa…</Vazio>;
  return <Editor perfil={perfil.data} />;
}

function Editor({ perfil }: { perfil: EmpresaPerfilResponse }) {
  const me = useMe();
  const editavel = podeEditarPerfil(me.data?.permissoes);
  const base = useMemo<EmpresaPerfilDto>(() => (perfil.atualizadoEm ? deResposta(perfil) : { ...FORM_VAZIO, ...deResposta(perfil), razaoSocial: '' }), [perfil]);
  const form = useForm<EmpresaPerfilDto, unknown, EmpresaPerfilDto>({ resolver: zodResolver(empresaPerfilSchema), defaultValues: base, mode: 'onTouched' });
  const { isDirty, dirtyFields, errors } = form.formState;
  const salvar = useSalvarEmpresaPerfil();
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const bloqueio = useBlockerDirty(isDirty);
  const [cepFalhou, setCepFalhou] = useState(false);

  /** Ao sair do CEP com 8 digitos: preenche Endereco, Cidade e UF SO se estiverem vazios. Falha nao bloqueia nada. */
  async function preencherPeloCep() {
    const cep = form.getValues('cep') ?? '';
    if (cep.replace(/\D/g, '').length !== 8) return;
    const endereco = await buscarCep(cep);
    if (!endereco) {
      setCepFalhou(true);
      return;
    }
    setCepFalhou(false);
    const opcoes = { shouldDirty: true } as const;
    if (!form.getValues('endereco') && endereco.logradouro) form.setValue('endereco', endereco.logradouro, opcoes);
    if (!form.getValues('cidade') && endereco.municipio) form.setValue('cidade', endereco.municipio, opcoes);
    if (!form.getValues('uf')) form.setValue('uf', endereco.uf, opcoes);
  }

  // Servidor mudou (salvou, logo enviada): o formulario volta a refletir o gravado, se nao houver edicao em curso.
  useEffect(() => {
    if (!form.formState.isDirty) form.reset(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const setarErroDeCampo = useCallback(
    (e: unknown) => {
      if (!(e instanceof ApiError)) return;
      for (const i of e.body?.issues ?? []) {
        const campo = i.path.split('.')[0] as keyof EmpresaPerfilDto;
        if (campo in FORM_VAZIO) form.setError(campo, { message: i.message });
      }
    },
    [form],
  );

  async function gravar(valores: EmpresaPerfilDto) {
    setErroSalvar(null);
    try {
      const gravado = await salvar.mutateAsync(valores);
      form.reset(deResposta(gravado));
      setSalvoEm(new Date());
    } catch (e) {
      setErroSalvar(mensagemErro(e));
      setarErroDeCampo(e);
    }
  }
  const gravarAgora = () => void form.handleSubmit(gravar, () => setErroSalvar('Corrija os campos destacados'))();
  const desabilitado = !editavel;
  const alterados = Object.keys(dirtyFields).length;

  return (
    <>
      <p className="text-ink-secondary">Dados que saem no topo do PDF do orçamento. Empresa: {perfil.empresaNome}.</p>

      {!editavel && me.data && (
        <p role="note" data-testid="aviso-somente-leitura" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
          Seu papel é somente leitura: só administrador ou consultor altera os dados da empresa.
        </p>
      )}
      {!perfil.atualizadoEm && (
        <p role="status" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
          O perfil ainda não foi preenchido. Enquanto isso, o PDF usa o nome e o CNPJ cadastrados da empresa.
        </p>
      )}

      <form onSubmit={(e) => { e.preventDefault(); gravarAgora(); }} noValidate className="flex flex-col gap-5">
        <section aria-label="Dados da empresa" className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 rounded-2xl border border-border bg-card p-5">
          <Campo form={form} nome="razaoSocial" rotulo="Razão social" desabilitado={desabilitado} maxLength={200} className="sm:col-span-2" placeholder={perfil.empresaNome} />
          <Campo form={form} nome="nomeFantasia" rotulo="Nome fantasia" desabilitado={desabilitado} maxLength={200} />
          <Campo form={form} nome="cnpj" rotulo="CNPJ" desabilitado={desabilitado} maxLength={18} placeholder="00.000.000/0000-00" inputMode="numeric" />
          <Campo form={form} nome="telefone" rotulo="Telefone" desabilitado={desabilitado} maxLength={40} inputMode="tel" />
          <Campo form={form} nome="email" rotulo="E-mail" desabilitado={desabilitado} maxLength={200} inputMode="email" />
          <Campo form={form} nome="site" rotulo="Site" desabilitado={desabilitado} maxLength={200} inputMode="url" />
        </section>

        <section aria-label="Localização" className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 rounded-2xl border border-border bg-card p-5">
          <Campo form={form} nome="endereco" rotulo="Endereço" desabilitado={desabilitado} maxLength={200} className="sm:col-span-2" />
          <Campo form={form} nome="cidade" rotulo="Cidade" desabilitado={desabilitado} maxLength={100} />
          <Campo form={form} nome="uf" rotulo="UF" desabilitado={desabilitado} maxLength={2} placeholder="SC" />
          <Campo
            form={form}
            nome="cep"
            rotulo="CEP"
            desabilitado={desabilitado}
            maxLength={9}
            placeholder="00000-000"
            inputMode="numeric"
            aoSair={() => void preencherPeloCep()}
            rodape={
              cepFalhou ? (
                <p role="status" className="mt-1 text-caption text-ink-muted">
                  Não foi possível buscar o CEP. Preencha manualmente.
                </p>
              ) : undefined
            }
          />
        </section>

        <section aria-label="Fiscal e contato" className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 rounded-2xl border border-border bg-card p-5">
          <Campo form={form} nome="inscricaoEstadual" rotulo="Inscrição estadual" desabilitado={desabilitado} maxLength={20} placeholder="Só dígitos ou ISENTO" />
          <Campo form={form} nome="inscricaoMunicipal" rotulo="Inscrição municipal" desabilitado={desabilitado} maxLength={20} inputMode="numeric" />
          <div>
            <Label htmlFor="p-regimeTributario" className={ROTULO}>
              Regime tributário
            </Label>
            <select
              id="p-regimeTributario"
              disabled={desabilitado}
              aria-invalid={errors.regimeTributario ? true : undefined}
              className="h-9 w-full rounded-md border border-input bg-card px-3 text-body text-ink outline-none focus-visible:border-primary focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50"
              {...form.register('regimeTributario')}
            >
              <option value="">Não informado</option>
              {REGIMES_TRIBUTARIOS.map((r) => (
                <option key={r} value={r}>
                  {ROTULO_REGIME[r]}
                </option>
              ))}
            </select>
            <MensagemCampo id="p-regimeTributario-erro" mensagem={errors.regimeTributario?.message} />
            <p className="mt-1 text-caption text-ink-muted">Só cadastro: nenhum cálculo usa o regime.</p>
          </div>
          <Campo form={form} nome="contatoNome" rotulo="Nome do contato" desabilitado={desabilitado} maxLength={120} />
          <div className="sm:col-span-2">
            <Label htmlFor="p-rodapePdf" className={ROTULO}>
              Rodapé do PDF
            </Label>
            <textarea
              id="p-rodapePdf"
              rows={3}
              maxLength={500}
              disabled={desabilitado}
              aria-invalid={errors.rodapePdf ? true : undefined}
              className="w-full rounded-md border border-input bg-card px-3 py-2 text-body text-ink outline-none focus-visible:border-primary focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50"
              {...form.register('rodapePdf')}
            />
            <MensagemCampo id="p-rodapePdf-erro" mensagem={errors.rodapePdf?.message} />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="p-sobre" className={ROTULO}>
              Sobre a empresa
            </Label>
            <textarea
              id="p-sobre"
              rows={5}
              maxLength={2000}
              disabled={desabilitado}
              aria-invalid={errors.sobre ? true : undefined}
              aria-describedby="p-sobre-ajuda p-sobre-erro"
              className="w-full rounded-md border border-input bg-card px-3 py-2 text-body text-ink outline-none focus-visible:border-primary focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50"
              {...form.register('sobre')}
            />
            <p id="p-sobre-ajuda" className="mt-1 text-caption text-ink-muted">
              Conte o mercado, os principais clientes, a estratégia de preço e a sazonalidade. A IA do sistema usa este texto para entender a empresa.
            </p>
            <MensagemCampo id="p-sobre-erro" mensagem={errors.sobre?.message} />
          </div>
        </section>
      </form>

      <LogoEmpresa perfil={perfil} editavel={editavel} />

      {editavel && (
        <RecordToolbar
          dirty={isDirty}
          saving={salvar.isPending}
          savedAt={salvoEm}
          erro={erroSalvar}
          canPrev={false}
          canNext={false}
          alteradosCount={isDirty ? alterados : undefined}
          onSave={gravarAgora}
          onUndo={() => {
            form.reset(base);
            setErroSalvar(null);
          }}
        />
      )}
      <SaidaComAlteracoesDialog {...bloqueio} />
    </>
  );
}

function Vazio({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="rounded-2xl border border-dashed border-border bg-card px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
